import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "rounded-lg border border-gray-200 bg-white p-4" }
const _hoisted_2 = { class: "mb-2" }
const _hoisted_3 = { class: "text-sm font-semibold text-gray-700" }
const _hoisted_4 = { class: "text-xs text-gray-500" }
const _hoisted_5 = {
  key: 1,
  class: "py-6 text-center text-xs italic text-gray-400"
}

import { computed } from 'vue';
import merge from 'lodash-es/merge';
import GenericChart from '@/components/charts/GenericChart.vue';
import useDefaultChartOptions from '@/components/charts/composables/useDefaultChartOptions';
import { i18n } from '@/plugins/internationalization/i18n';
import type { WeekDataDetails } from '@/types';


export default /*@__PURE__*/_defineComponent({
  __name: 'LifeCycleChart',
  props: {
    details: {},
    insertedDate: {},
    insertedCount: {},
    avgWeight: {},
    isWeaners: { type: Boolean }
  },
  setup(__props: any) {

const props = __props;

// Static defaults only (fonts/colours/tooltip) - the batch-dependent parts (categories, series)
// are merged in per render below, since this composable snapshots whatever is passed in once and
// would otherwise go stale when the selected batch changes length.
const staticDefaults = useDefaultChartOptions({}, {}, false);

const chartOptions = computed(() => {
    const base: Record<string, unknown> = merge({} as Record<string, unknown>, staticDefaults.value);

    // x-axis is "number of weeks after insertion", week 0 = the insertion week - a plain
    // category axis over the row index, not the underlying dates; the base default assumes a
    // datetime axis tied to the period picker, which doesn't apply here.
    return merge(base, {
        chart: { height: 320 },
        xAxis: {
            type: 'category',
            categories: props.details.map((_, index) => `${index}`),
            min: null,
            max: null,
            tickInterval: null,
            labels: { rotation: 0, format: '{value}' },
        },
        yAxis: { allowDecimals: false, softMin: 0 },
        legend: { enabled: true },
        tooltip: { shared: true },
        // The shared default's plotOptions tooltip formats `x` as a date (this.x is a week index
        // here, not a timestamp) - override it so the shared tooltip reads "Week N" per series.
        plotOptions: {
            series: {
                tooltip: {
                    pointFormatter(this: any): string {
                        return `<span style="font-size: 10px">${i18n.global.t('views.weekOverview.lifeCycle.tableHeadings.week')} ${this.x}</span><br/><span style="color:${this.color}">●</span> ${this.series.name}: <b>${i18n.global.n(this.y)}</b><br/>`;
                    },
                },
            },
        },
        series: [
            {
                type: 'line',
                name: i18n.global.t('views.weekOverview.lifeCycle.seriesRemaining') as string,
                data: props.details.map((detail) => detail.remaining),
                marker: { enabled: false },
            },
            {
                type: 'column',
                name: i18n.global.t('views.weekOverview.lifeCycle.seriesDead') as string,
                data: props.details.map((detail) => detail.dead),
                borderRadius: 1,
            },
            ...(props.isWeaners
                ? []
                : [
                      {
                          type: 'column',
                          name: i18n.global.t('views.weekOverview.lifeCycle.seriesSlaughtered') as string,
                          data: props.details.map((detail) => detail.slaughtered),
                          borderRadius: 1,
                      },
                  ]),
            {
                type: 'column',
                name: i18n.global.t('views.weekOverview.lifeCycle.seriesTransfered') as string,
                data: props.details.map((detail) => detail.transferred),
                borderRadius: 1,
            },
            {
                type: 'column',
                name: i18n.global.t('views.weekOverview.lifeCycle.seriesDeparted') as string,
                // Weaners have no slaughtered series; their slaughtered exits count as sold here, as in the table/KPIs.
                data: props.details.map((detail) => detail.departed + (props.isWeaners ? (detail.slaughtered ?? 0) : 0)),
                borderRadius: 1,
            },
        ],
    });
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, _toDisplayString(_ctx.$t('views.weekOverview.lifeCycle.chartTitle', { date: _ctx.insertedDate })), 1),
      _createElementVNode("div", _hoisted_4, _toDisplayString(_ctx.$t('views.weekOverview.lifeCycle.chartTitleDetail', { count: _ctx.$n(_ctx.insertedCount || 0), weight: Number.isFinite(_ctx.avgWeight) ? _ctx.$n(_ctx.avgWeight, 'decimal') : '—' })), 1)
    ]),
    (_ctx.details.length)
      ? (_openBlock(), _createBlock(GenericChart, {
          key: 0,
          options: (chartOptions.value as any),
          expandable: false,
          "destroy-on-update": true
        }, null, 8, ["options"]))
      : (_openBlock(), _createElementBlock("p", _hoisted_5, _toDisplayString(_ctx.$t('views.weekOverview.texts.noData')), 1))
  ]))
}
}

})